import { useLayoutEffect, useState } from 'react';
import { Flower2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

export function HomeEnhancements() {
  const [libraryMount, setLibraryMount] = useState<HTMLElement | null>(null);
  const [featureTarget, setFeatureTarget] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    let mounted = true;
    let insertedLibraryMount: HTMLElement | null = null;

    const refreshTargets = () => {
      if (!mounted) return;

      const faq = document.querySelector<HTMLElement>('.faq-section');
      if (faq) {
        if (!insertedLibraryMount || !insertedLibraryMount.isConnected) {
          insertedLibraryMount = document.createElement('div');
          insertedLibraryMount.className = 'home-library-mount';
          faq.parentElement?.insertBefore(insertedLibraryMount, faq);
        }
        setLibraryMount(insertedLibraryMount);
      }

      setFeatureTarget(document.querySelector<HTMLElement>('.feature-strip'));
      document.body.classList.toggle('plant-analysis-running', Boolean(document.querySelector('.analysis-loading')));
    };

    refreshTargets();
    const observer = new MutationObserver(refreshTargets);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      mounted = false;
      observer.disconnect();
      document.body.classList.remove('plant-analysis-running');
      insertedLibraryMount?.remove();
    };
  }, []);

  useLayoutEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      document.documentElement.classList.add('reduced-motion');
      return () => document.documentElement.classList.remove('reduced-motion');
    }

    const revealSelector = [
      '.hero .eyebrow',
      '.hero h1',
      '.hero > p',
      '.hero-actions',
      '.workspace > .section-intro',
      '.search-box',
      '.feature-strip > article',
      '.home-library-link',
      '.faq-head',
      '.faq-card',
    ].join(',');

    const observed = new Set<Element>();
    const revealObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('flow-in');
        revealObserver.unobserve(entry.target);
      }
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    const connectReveals = () => {
      const groups = new Map<Element, number>();
      document.querySelectorAll<HTMLElement>(revealSelector).forEach((element) => {
        if (observed.has(element)) return;
        observed.add(element);

        const parent = element.parentElement;
        const order = parent ? (groups.get(parent) || 0) : 0;
        if (parent) groups.set(parent, order + 1);

        element.classList.add('flow-reveal');
        element.style.setProperty('--flow-order', String(Math.min(order, 5)));
        revealObserver.observe(element);
      });
    };

    connectReveals();
    const mutationObserver = new MutationObserver(connectReveals);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    const hero = document.querySelector<HTMLElement>('.hero');
    let frame = 0;
    const updateHeroProgress = () => {
      frame = 0;
      if (!hero) return;
      const rect = hero.getBoundingClientRect();
      const travel = Math.max(1, rect.height * 0.78);
      const progress = Math.max(0, Math.min(1, -rect.top / travel));
      hero.style.setProperty('--hero-progress', progress.toFixed(3));
    };
    const scheduleHeroProgress = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateHeroProgress);
    };

    updateHeroProgress();
    window.addEventListener('scroll', scheduleHeroProgress, { passive: true });
    window.addEventListener('resize', scheduleHeroProgress);

    return () => {
      revealObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('scroll', scheduleHeroProgress);
      window.removeEventListener('resize', scheduleHeroProgress);
      if (frame) window.cancelAnimationFrame(frame);
      hero?.style.removeProperty('--hero-progress');
      observed.forEach((element) => {
        element.classList.remove('flow-reveal', 'flow-in');
        (element as HTMLElement).style.removeProperty('--flow-order');
      });
    };
  }, []);

  return (
    <>
      {featureTarget && createPortal(
        <article className="community-feature-card">
          <Flower2 />
          <h3>Share what you grow</h3>
          <p>Publish a saved plant to the Community Garden, add a note, and discover what other plant lovers are growing.</p>
        </article>,
        featureTarget,
      )}

      {libraryMount && createPortal(
        <section className="library-discovery-link home-library-link" aria-label="Plant library">
          <div>
            <span>Explore by name</span>
            <strong>Already know your plant?</strong>
            <p>Browse every public PlantGrow care guide in one searchable library, including photos shared by the community.</p>
          </div>
          <Link to="/plants">Browse plant library</Link>
        </section>,
        libraryMount,
      )}
    </>
  );
}
